import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import './FormViewer.css';

function FormViewer({ sessionId, onClose }) {
  const { t } = useTranslation();
  const [htmlContent, setHtmlContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) loadForm();
  }, [sessionId]);

  const loadForm = async () => {
    if (!sessionId) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await mvpApi.getFormHTML(sessionId);
      const text = await response.data.text();
      setHtmlContent(text);
    } catch (err) {
      console.error('Error loading form:', err);
      setError(t('formViewer.error', 'Error loading form'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `immigration_form_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  return (
    <div className="mvp-form-viewer-overlay" onClick={onClose}>
      <div className="mvp-form-viewer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mvp-form-viewer-header">
          <h2>{t('dataPanel.viewForm', 'View form')}</h2>
          <div className="mvp-form-viewer-actions">
            {htmlContent && (
              <>
                <button type="button" className="mvp-form-viewer-btn mvp-form-viewer-print" onClick={handlePrint}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  {t('formViewer.print', 'Print')}
                </button>
                <button type="button" className="mvp-form-viewer-btn mvp-form-viewer-download" onClick={handleDownload}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t('formViewer.download', 'Download')}
                </button>
              </>
            )}
            <button type="button" className="mvp-form-viewer-close" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="mvp-form-viewer-content">
          {isLoading ? (
            <div className="mvp-form-viewer-loading">
              <div className="mvp-form-viewer-spinner" />
              <p>{t('loading')}...</p>
            </div>
          ) : error ? (
            <div className="mvp-form-viewer-error">
              <p>{error}</p>
              <button type="button" onClick={loadForm}>{t('formViewer.retry', 'Retry')}</button>
            </div>
          ) : htmlContent ? (
            <iframe srcDoc={htmlContent} title="Immigration Form" className="mvp-form-viewer-iframe" />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default FormViewer;
